import {
  getInstrumentIdForce,
  InstrumentSubscriptionDataScope,
  InstrumentSubscriptionRequestItem,
  ListedInstrument,
  ListedInstrumentLike,
  SubscribeOptions,
} from '../../types';
import { isArrayContainsAnother, ObjectSet, sortArray } from '../../utils';

const getDataScopesAsId = (dataScopes: Array<InstrumentSubscriptionDataScope>) => sortArray(dataScopes).join(',');
const getIdForSubscriptionSet = (item: SubscribeOptions | InstrumentSubscriptionRequestItem) =>
  (item as SubscribeOptions).instrument
    ? `${getInstrumentIdForce((item as SubscribeOptions).instrument)}/${getDataScopesAsId(
        (item as SubscribeOptions).dataScope,
      )}`
    : `${getInstrumentIdForce(item as InstrumentSubscriptionRequestItem)}/${getDataScopesAsId(
        (item as InstrumentSubscriptionRequestItem).datascopes,
      )}`;

export const getInstrumentsToQuery = (
  subscriptionChange: Array<{ direction: 'subscribe' | 'unsubscribe'; option: SubscribeOptions }>,
  currentSubscriptions: Array<InstrumentSubscriptionRequestItem>,
): {
  newlyRequestedInstruments: Array<string>;
  alreadySubscribedInstruments: Array<string>;
  newlyRequestedInstrumentList: Array<ListedInstrument | ListedInstrumentLike>;
  addedSubscriptions: ObjectSet<SubscribeOptions>;
  unsubscribeList: ObjectSet<SubscribeOptions>;
  forceRequestStaticData: {
    instruments: Array<ListedInstrument | ListedInstrumentLike>;
    dataScope: Array<InstrumentSubscriptionDataScope>;
  };
} => {
  const forceRequestStaticData: {
    instruments: Array<ListedInstrument | ListedInstrumentLike>;
    dataScope: Array<InstrumentSubscriptionDataScope>;
  } = {
    instruments: [],
    dataScope: [],
  };
  const unsubscribeList: ObjectSet<SubscribeOptions> = new ObjectSet<SubscribeOptions>(getIdForSubscriptionSet);
  const addedSubscriptions = new ObjectSet<SubscribeOptions>(getIdForSubscriptionSet);

  subscriptionChange.forEach(change => {
    if (change.direction === 'subscribe') {
      addedSubscriptions.add(change.option);
      unsubscribeList.delete(change.option);

      // if static data is required as well, and not already added to requested static data list add to it
      if (
        change.option.forceRequestStaticData &&
        !forceRequestStaticData.instruments.find(
          _ => getInstrumentIdForce(_) === getInstrumentIdForce(change.option.instrument),
        )
      ) {
        forceRequestStaticData.instruments.push(change.option.instrument);
        forceRequestStaticData.dataScope = change.option.dataScope;
      }
    } else {
      unsubscribeList.add(change.option);
      addedSubscriptions.delete(change.option);
    }
  });
  const newlyRequestedInstruments: Array<string> = [];
  const alreadySubscribedInstruments: Array<string> = [];
  const newlyRequestedInstrumentList: Array<ListedInstrument | ListedInstrumentLike> = [];

  const instrumentIdsToRemoveFromAddedSubscriptions = new Array<SubscribeOptions>();
  addedSubscriptions.forEach(subscribeOption => {
    const instrumentId = getInstrumentIdForce(subscribeOption.instrument);
    const isAlreadySubscribed = currentSubscriptions.some(
      currentSubscription =>
        getInstrumentIdForce(currentSubscription) === instrumentId &&
        isArrayContainsAnother(currentSubscription.datascopes, subscribeOption.dataScope),
    );
    if (isAlreadySubscribed) {
      alreadySubscribedInstruments.push(instrumentId);
      instrumentIdsToRemoveFromAddedSubscriptions.push(subscribeOption);
    } else {
      newlyRequestedInstruments.push(instrumentId);
      newlyRequestedInstrumentList.push(subscribeOption.instrument);
    }
  });
  instrumentIdsToRemoveFromAddedSubscriptions.forEach(instrumentId => addedSubscriptions.delete(instrumentId));

  return {
    newlyRequestedInstruments,
    newlyRequestedInstrumentList,
    alreadySubscribedInstruments,
    addedSubscriptions,
    unsubscribeList,
    forceRequestStaticData,
  };
};
